/**
 * Copy of the AMS View list side, shell and analytics
 * (`components/Ams/view/{shell,list,analytics}`). Spread into `en.amsView`;
 * owns the `filters`, `tabs`, `caption`, `adGroupTable`, `campaignTable`,
 * `overallTrend`, `analytics`, `export` and `infoDrawer` sub-keys. VentaHub's
 * English, verbatim where VentaHub had it (cited per block), adapted only
 * where a hidden feature (decision 2) changed what it says.
 */
export const amsViewList = {
  filters: {
    label: 'AMS View filters',
    // AmsViewPage.tsx:2035-2046
    dateRange: 'Date range',
    // AmsViewPage.tsx:2148-2177
    go: 'Go',
    refresh: 'Refresh',
    loading: 'Loading…',
    // AmsViewPage.tsx:2182-2322 — the second, narrowing row.
    advancedLabel: 'Narrow the loaded ad groups',
    search: 'Search ad group / ASIN / keyword',
    campaign: 'Campaign',
    campaignPlaceholder: 'Search campaign',
    campaigns_one: '{{n}} campaign',
    campaigns_other: '{{n}} campaigns',
    status: 'Status',
    adGroupType: 'Ad Group Type',
    targeting: 'Targeting',
    clear: 'Clear filters',
    // AmsViewPage.tsx:1570-1598
    loader_one: 'Loading {{n}} store',
    loader_other: 'Loading {{n}} stores',
  },
  // AmsViewPage.tsx:2327-2391, 2535-2586 (Placements hidden).
  tabs: {
    label: 'AMS View sections',
    adgroups: 'Adgroups',
    campaigns: 'Campaigns',
    asins: 'ASINs',
    targets: 'Targets',
    searchTerms: 'Search Terms',
    negativeKeywords: 'Negative Keywords',
    altProducts: 'Alt Products',
    analytics: 'Analytics',
    // AmsViewPage.tsx:2478-2496
    scopedTo: 'Scoped to',
    clearDrill: 'Clear selected ad group',
    clearDrillTip: 'Clear the selected ad group',
  },
  // AmsViewPage.tsx:2436-2477; the USD text adapted: nothing is converted (decision 2).
  caption: {
    adGroups_one: '{{n}} ad group',
    adGroups_other: '{{n}} ad groups',
    campaigns_one: '{{n}} campaign',
    campaigns_other: '{{n}} campaigns',
    filteredFrom: '(filtered from {{total}})',
    filteredFromAdGroups: '(filtered from {{total}} ad groups)',
    severalCurrenciesAdGroups:
      'Several currencies · sorting compares native amounts · totals are per currency',
    severalCurrenciesCampaigns: 'Several currencies · sorting compares native amounts',
  },
  // components/AdGroupTable.tsx
  adGroupTable: {
    label: 'Ad groups',
    columns: {
      adGroup: 'Ad Group',
      status: 'Status',
      refreshedAt: 'Refreshed at',
    },
    // AmsViewPage.tsx:1638-1701
    notSearched: 'Select at least one store and press Go to load ad group performance.',
    empty: 'No ad groups match the current filters.',
    // :412-445
    history: 'View change history',
    historyOf: 'History of {{name}}',
    details: 'View ad group details',
    detailsOf: 'View details for {{name}}',
    // :997-1031 — the target under a CTR, ACOS or Conv. cell.
    target: 'Target {{value}}',
    targetShort: '/ {{value}}',
    // components/DataRefreshedCell.tsx:39-96, utils/dateTime.ts:371-388
    refreshed: {
      noData: 'No data for this ad group in the selected period',
      justNow: 'just now',
      minutes: '{{count}} min ago',
      hours: '{{count}} hr ago',
      days: '{{count}}d ago',
      months: '{{count}}mo ago',
      years: '{{count}}y ago',
    },
    // :615-890 — the row's accordion.
    panel: {
      label: 'Details of {{name}}',
      lastData: 'Last data {{date}}',
      alternateSales: 'Alternate sales {{amount}}',
      store: 'Store',
      type: 'Type',
      targeting: 'Targeting',
      category: 'Category',
      categoryPath: '{{category}} › {{subcategory}}',
      firstSeen: 'First seen',
      firstSeenValue: '{{date}} · {{age}}',
      campaign: 'Campaign',
      campaignId: 'Campaign ID',
      // :188-197 ageLabel
      age: {
        today: 'Today',
        days_one: '{{count}} day',
        days_other: '{{count}} days',
        months: '{{count}} mo',
        years: '{{years}}y {{months}}mo',
      },
      // :744-783 — the drill list (Placements hidden).
      drill: {
        label: 'Open a detail tab',
        asins: 'ASINs',
        targets: 'Targets',
        searchTerms: 'Search Terms',
        negatives: 'Negatives',
        altProducts: 'Alt Products',
        altProductsTip:
          "Purchases of OTHER ASINs than the advertised one, attributed to this ad group's ads (purchased-product report).",
        open: '{{tab}} of {{name}}',
      },
      // :797-811 — the campaign budget bar (:812-840) is hidden (decision 2).
      budget: {
        title: 'Ad-group daily budget',
        subtitle: '{{avg}} avg/day over last 7 days · {{budget}} budget',
        tip: 'Daily budget configured on the ad-group master vs average daily spend over the 7 most recent dates with data.',
      },
      // :843-887
      topAsins: {
        title: 'Top ASINs by sales',
        failed: 'Failed to load top ASINs.',
        empty: 'No ASIN sales in the selected range.',
        units_one: '{{sales}} · {{n}} unit',
        units_other: '{{sales}} · {{n}} units',
      },
    },
  },
  // components/CampaignTable.tsx (Daily Budget, Used and Budget updated at hidden).
  campaignTable: {
    label: 'Campaigns',
    columns: {
      campaign: 'Campaign',
      store: 'Store',
      status: 'Status',
    },
    // :334-339
    adGroups_one: '{{n}} ad group · ID {{id}}',
    adGroups_other: '{{n}} ad groups · ID {{id}}',
    historyOf: 'History of campaign {{name}}',
    // AmsViewPage.tsx:1703-1746
    empty: 'No campaigns match the current filters.',
    // :538-697
    sub: {
      caption: 'Ad groups in this campaign · click a row to drill in',
      label: 'Ad groups in {{name}}',
      adGroup: 'Ad group',
      status: 'Status',
      dailyBudget: 'Daily budget',
      dailyBudgetTip:
        'This column is meant as a reference and does not represent actual budget limit. We use this as a reference in the marketing.',
      spend: 'Spend (window)',
      sales: 'Sales (window)',
    },
  },
  // components/OverallTrendChart.tsx (live, hourly, attribution and the currency picker hidden).
  overallTrend: {
    title: 'Overall trend',
    empty: 'No data for the selected range.',
    seriesInCurrency: '{{metric}} · {{currency}}',
  },
  // components/AmsAnalyticsPanel.tsx
  analytics: {
    empty: 'Pick a store and date range to see analytics.',
    // fe-arch §7.2: money is never added across currencies, so the money
    // widgets show one currency's ad groups at a time.
    currency: 'Currency',
    currencyNote_one:
      'Showing {{currency}} ad groups; {{n}} in other currencies is left out of money charts',
    currencyNote_other:
      'Showing {{currency}} ad groups; {{n}} in other currencies are left out of money charts',
    help: 'About {{title}}',
    expand: 'Expand {{title}}',
    // :63-99
    widgets: {
      spendVsSales: {
        title: 'Spend vs Sales',
        description:
          "Each dot is an ad group. Sales above the dashed break-even line are profitable (ACOS ≤ your margin rate). Below the line, you're spending more on ads than you earn.",
      },
      targetAchievement: {
        title: 'Target Achievement',
        description:
          'For ad groups that have a target set on the master record, how many are meeting it. ACOS is met when actual ≤ target × 1.1; CTR and Conv. when actual ≥ target × 0.9.',
      },
      ctrCvr: {
        title: 'CTR vs Conversion Rate',
        description:
          'Each dot is an ad group. The X-axis is click-through rate; the Y-axis is conversion rate; bubble size is spend. High-traffic but low-conversion ad groups appear bottom-right.',
      },
      spendDistribution: {
        title: 'Spend Distribution',
        description:
          'Share of total spend across the top 8 ad groups (everything else folded into "Other"). Use to spot ad groups eating a disproportionate share of budget.',
      },
      topPerformers: {
        title: 'Top Performers',
        description:
          'Top 5 ad groups by ROAS and by Sales. The inline bar visualizes their value relative to the leader of the list.',
      },
      funnel: {
        title: 'Conversion Funnel',
        description:
          'Aggregate Impressions → Clicks → Units across all visible ad groups, with the click-through rate and conversion rate between each step.',
      },
      cpcHistogram: {
        title: 'CPC Distribution',
        description:
          'How many ad groups fall into each cost-per-click band. Heavy tails on the right indicate expensive keywords.',
      },
    },
    // :346-380, :546-591
    axes: {
      spend: 'Spend ({{currency}})',
      sales: 'Sales ({{currency}})',
      ctr: 'CTR (%)',
      conversion: 'Conv. (%)',
      bubble: 'Spend',
    },
    breakEven: 'Break-even (margin {{pct}}%)',
    other: 'Other',
    // :593-662
    target: {
      meeting: '{{met}} / {{total}} meeting target',
      without: '{{count}} without target',
      met: '{{pct}}% met',
      none: 'No ad groups have targets configured on the master record yet.',
    },
    // :410-487
    top: {
      byRoas: 'By ROAS',
      bySales: 'By Sales',
      noData: 'No data',
    },
    // :489-512
    funnel: {
      impressions: 'Impressions',
      clicks: 'Clicks',
      units: 'Units',
      count: 'Count',
      rate: '{{stage}}: {{value}} · {{rate}}',
    },
    // :514-544
    cpc: {
      under: '< {{max}}',
      between: '{{min}} – {{max}}',
      over: '> {{min}}',
      adGroups: 'Ad groups',
    },
  },
  // The header's two actions: AmsViewPage.tsx:1948-1977, components/ExportButton.tsx:83-122.
  export: {
    changeHistory: 'Change history',
    button: 'Export',
    pending: 'Exporting…',
    // "8 sheets" became 7: the Placements sheet is hidden (decision 2).
    tooltip: 'Export this view to Excel (7 sheets) — per ad group, or aggregated across ad groups',
    byAdGroup: 'Excel — by ad group',
    aggregated: 'Excel — aggregated',
    failed: 'Failed to start export',
  },
  // components/AdGroupInfoDrawer.tsx (Campaign budget hidden, decision 2).
  infoDrawer: {
    close: 'Close ad group details',
    description: 'Master fields of this ad group',
    fields: {
      adGroupId: 'Ad group ID',
      adGroupType: 'Ad group type',
      targetingType: 'Targeting type',
      status: 'Ad group status',
      campaign: 'Campaign',
      campaignId: 'Campaign ID',
      campaignStatus: 'Campaign status',
      store: 'Store',
      storeId: 'Store ID',
      profileId: 'Profile ID',
      brand: 'Brand',
      brandId: 'Brand ID',
      currency: 'Currency',
      category: 'Category',
      subcategory: 'Subcategory',
      budget: 'Ad group budget',
      targetAcos: 'Target ACOS',
      targetCtr: 'Target CTR',
      targetConversion: 'Target conversion',
      asp: 'ASP',
      firstData: 'First data',
      lastData: 'Last data',
    },
    sisters: {
      exactMatch: 'Exact match ad group',
      productTarget: 'Product target ad group',
      branded: 'Branded ad group',
      competition: 'Competition ad group',
      longTail: 'Long tail ad group',
      highPriority: 'High priority ad group',
    },
    sisterValue: '{{name}} ({{id}})',
    percent: '{{value}}%',
  },
} as const
